import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import {
  Video, MessageSquare, Users2, Clock, ShieldCheck, Send, AlertCircle, PhoneOff,
  FileText, Upload, Trash2, CheckCircle2, CircleDashed, Download, ClipboardCheck, FileDown,
  UserCheck, MapPin, Mail, MailCheck,
} from "lucide-react";

const OPS_DOC_CATEGORIES = [
  { value: "sop", label: "Standard Operating Procedures" },
  { value: "risk", label: "Risk Assessment" },
  { value: "emergency", label: "Emergency Procedures" },
  { value: "arrival", label: "Arrival & Site Access" },
  { value: "equipment", label: "Equipment & Kit Brief" },
  { value: "declaration", label: "Digital Declaration" },
  { value: "other", label: "Other" },
];

export default function OpsRoom() {
  const { bookingId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState("");

  const load = async () => {
    try { const r = await api.get(`/bookings/${bookingId}/ops-room`); setData(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [bookingId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post(`/bookings/${bookingId}/ops-room/messages`, { text }); setText(""); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="txt-lo p-6">Loading…</div>;
  const b = data.booking;
  const isManaged = b.ops_room_option === "managed";
  const tierKind = isManaged ? "managed" : (b.ops_room_option === "tracked" ? "tracked" : "match");

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6" data-testid="ops-room-page">
      <div className="page-hero flex items-start justify-between gap-4 flex-wrap">
        <div className="relative z-10">
          <div className="eyebrow eyebrow-brand mb-2">{isManaged ? "Managed Deployment" : "Operations Room"}</div>
          <h1 className="text-3xl font-outfit font-bold txt-hi tracking-tight">#{bookingId.slice(0, 8)}</h1>
          <div className="txt-md text-sm font-manrope mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>{b.company_name}</span>
            <span className="txt-mute">·</span>
            <span>{b.workers} pax</span>
            <span className="txt-mute">·</span>
            <span>{b.hours}h</span>
            <span className="txt-mute">·</span>
            <span>€{b.hourly_rate}/hr</span>
            {b.date && <><span className="txt-mute">·</span><span>{b.date}</span></>}
          </div>
        </div>
        <div className="flex items-center gap-2 relative z-10">
          <span className={`spill spill-${tierKind}`}><span className="dot" /> {isManaged ? "Managed" : (b.ops_room_option === "tracked" ? "Tracked" : "Match")}</span>
          <span className="spill spill-live"><span className="dot" /> Live</span>
          <OpsVideoButton bookingId={bookingId} />
          <Link to="/company/bookings" className="text-sm text-brand font-manrope font-semibold hover:underline">← Bookings</Link>
        </div>
      </div>

      <Tabs defaultValue="chat" className="space-y-4 tabs-dark">
        <TabsList data-testid="ops-tabs">
          <TabsTrigger value="chat" data-testid="ops-tab-chat"><MessageSquare className="w-4 h-4 mr-1" /> Chat</TabsTrigger>
          <TabsTrigger value="documents" data-testid="ops-tab-documents"><FileText className="w-4 h-4 mr-1" /> Documents</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="ops-tab-compliance"><ClipboardCheck className="w-4 h-4 mr-1" /> Compliance</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="ops-tab-attendance"><MapPin className="w-4 h-4 mr-1" /> Attendance</TabsTrigger>
          <TabsTrigger value="team" data-testid="ops-tab-team"><Users2 className="w-4 h-4 mr-1" /> Team</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="ops-tab-timeline"><Clock className="w-4 h-4 mr-1" /> Timeline</TabsTrigger>
        </TabsList>

        <TabsContent value="chat">
          <div className="card-dark p-4 h-[420px] overflow-y-auto space-y-3 font-manrope text-sm" data-testid="ops-chat-list">
            {(data.messages || []).length === 0 ? (
              <div className="txt-lo text-center py-16">No messages yet.</div>
            ) : (data.messages || []).map((m) => (
              <div key={m.id} className={`flex flex-col ${m.user_id === user.id ? "items-end" : "items-start"}`} data-testid={`ops-msg-${m.id}`}>
                <div className="eyebrow mb-0.5">
                  {m.author_name} · {new Date(m.created_at).toLocaleTimeString()}
                </div>
                <div className={`inline-block max-w-[80%] rounded-lg px-3 py-2 ${m.user_id === user.id ? "bg-brand text-white" : "bg-white/5 border border-white/10 txt-hi"}`}>
                  {m.text}
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Message team…" data-testid="ops-msg-input" />
            <Button onClick={send} disabled={busy || !text.trim()} className="btn-brand" data-testid="ops-msg-send">
              <Send className="w-4 h-4 mr-1" /> Send
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="documents">
          <DocumentCentre bookingId={bookingId} data={data} onChange={load} />
        </TabsContent>

        <TabsContent value="compliance">
          <ComplianceMatrix bookingId={bookingId} canManage={!!data.can_manage_docs} />
        </TabsContent>

        <TabsContent value="attendance">
          <AttendanceRoster bookingId={bookingId} canManage={!!data.can_manage_docs} />
        </TabsContent>

        <TabsContent value="team">
          <div className="grid sm:grid-cols-2 gap-3">
            {(data.participants || []).map((p) => (
              <div key={p.id} className="card-flat p-4 flex items-center gap-3" data-testid={`ops-participant-${p.secusource_id}`}>
                <div className="w-10 h-10 rounded-md bg-brand-light text-brand flex items-center justify-center font-outfit font-bold">
                  {(p.full_name || p.company_name || p.secusource_id || "?").charAt(0)}
                </div>
                <div>
                  <div className="font-outfit font-semibold text-white">{p.full_name || p.company_name || "—"}</div>
                  <div className="text-xs text-white/60 font-manrope">{p.secusource_id} · {p.role.replace(/_/g, " ")}</div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="timeline">
          <div className="card-flat p-4">
            <div className="space-y-3 font-manrope text-sm">
              {(data.timeline || []).map((t, i) => (
                <div key={i} className="flex items-start gap-3" data-testid={`ops-timeline-${i}`}>
                  <div className="w-2 h-2 rounded-full bg-brand mt-2 flex-shrink-0" />
                  <div>
                    <div className="font-outfit font-semibold text-white">{t.event}</div>
                    <div className="text-xs text-white/60">{t.at ? new Date(t.at).toLocaleString() : ""} {t.detail ? " · " + t.detail : ""}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <div className="rounded-lg bg-brand-light/60 border border-brand/30 p-4 text-sm text-white font-manrope" data-testid="ops-phase-notice">
        <ShieldCheck className="w-4 h-4 inline mr-1 text-brand" />
        Phase 4 & 5 live · Attendance tracking, Document Centre, per-participant acknowledgements and audit-grade Compliance PDF are all active.
      </div>
    </div>
  );
}

/* ---------------- Phase 3: Document Centre ---------------- */
function DocumentCentre({ bookingId, data, onChange }) {
  const [busy, setBusy] = useState(false);
  const [ackBusyId, setAckBusyId] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("sop");
  const [requiredAck, setRequiredAck] = useState(true);
  const [file, setFile] = useState(null);
  const canManage = !!data.can_manage_docs;
  const docs = data.documents || [];

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const authFetch = async (url, opts = {}) => fetch(url, { credentials: "include", ...opts });

  const upload = async (e) => {
    e.preventDefault();
    if (!file || !title.trim()) { toast.error("Title and file are required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title.trim());
      fd.append("category", category);
      fd.append("required_ack", requiredAck ? "true" : "false");
      await api.post(`/bookings/${bookingId}/ops-room/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setTitle(""); setCategory("sop"); setRequiredAck(true); setFile(null);
      const inp = document.getElementById("ops-doc-file-input"); if (inp) inp.value = "";
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  };

  const ackDoc = async (docId) => {
    setAckBusyId(docId);
    try {
      await api.post(`/bookings/${bookingId}/ops-room/documents/${docId}/ack`, {});
      toast.success("Marked as read");
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to acknowledge");
    } finally { setAckBusyId(null); }
  };

  const removeDoc = async (docId) => {
    if (!window.confirm("Delete this document? All acknowledgements for it will be removed.")) return;
    try {
      await api.delete(`/bookings/${bookingId}/ops-room/documents/${docId}`);
      toast.success("Document removed");
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const downloadDoc = async (docId, filename) => {
    // Uses cookie auth (same-origin) — falls back to bearer via api client if needed
    const url = `${backendUrl}/api/bookings/${bookingId}/ops-room/documents/${docId}/download`;
    try {
      const res = await authFetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename || `document-${docId}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    } catch (err) {
      toast.error("Download failed. If you are on a shared device, sign in again.");
    }
  };

  return (
    <div className="space-y-4">
      {canManage && (
        <form onSubmit={upload} className="card-flat p-4 space-y-3" data-testid="ops-doc-upload-form">
          <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold">Upload document</div>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="text-xs font-outfit font-bold text-white/70 uppercase tracking-widest">Title</label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Site SOP — Level 1 access" data-testid="ops-doc-title" />
            </div>
            <div>
              <label className="text-xs font-outfit font-bold text-white/70 uppercase tracking-widest">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full h-10 rounded-md border border-white/15 bg-[#0A0A0A] px-3 text-sm font-manrope text-white"
                data-testid="ops-doc-category"
              >
                {OPS_DOC_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-outfit font-bold text-white/70 uppercase tracking-widest">File</label>
              <input id="ops-doc-file-input" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)}
                     className="block w-full text-sm text-white/85 font-manrope file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-brand file:text-white file:font-outfit file:font-bold hover:file:bg-brand-hover"
                     data-testid="ops-doc-file" />
              <div className="text-[11px] text-white/60 mt-1">Max 20MB. PDF, images and docs supported.</div>
            </div>
            <label className="flex items-center gap-2 text-sm text-white font-manrope mt-6">
              <input type="checkbox" checked={requiredAck} onChange={(e) => setRequiredAck(e.target.checked)} data-testid="ops-doc-required-ack" />
              Require every staff member to acknowledge
            </label>
          </div>
          <Button type="submit" disabled={busy || !file || !title.trim()} className="bg-brand hover:bg-brand-hover text-white" data-testid="ops-doc-upload-btn">
            <Upload className="w-4 h-4 mr-1" /> {busy ? "Uploading…" : "Upload document"}
          </Button>
        </form>
      )}

      {docs.length === 0 ? (
        <div className="card-flat p-8 text-center text-white/60 font-manrope text-sm">
          No documents yet. {canManage ? "Upload SOPs, risk assessments and briefing packs above." : "The company or team lead will upload SOPs and briefings here."}
        </div>
      ) : (
        <div className="space-y-3">
          {docs.map((d) => (
            <div key={d.id} className="card-flat p-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between" data-testid={`ops-doc-${d.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-brand font-outfit font-bold">{d.category_label}</div>
                <div className="font-outfit font-semibold text-white truncate">{d.title}</div>
                <div className="text-xs text-white/60 font-manrope">
                  Uploaded by {d.uploaded_by_name || "—"} · {d.uploaded_at ? new Date(d.uploaded_at).toLocaleString() : ""}
                  {d.required_ack ? " · " : ""}
                  {d.required_ack && <span className="text-amber-600 font-semibold">Acknowledgement required</span>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <Button variant="outline" onClick={() => downloadDoc(d.id, d.original_filename)} data-testid={`ops-doc-download-${d.id}`}>
                  <Download className="w-4 h-4 mr-1" /> Download
                </Button>
                {d.required_ack && (
                  d.acked_by_me ? (
                    <span className="inline-flex items-center gap-1 text-gold-500 text-sm font-manrope font-semibold" data-testid={`ops-doc-acked-${d.id}`}>
                      <CheckCircle2 className="w-4 h-4" /> Read &amp; understood
                    </span>
                  ) : (
                    <Button
                      onClick={() => ackDoc(d.id)}
                      disabled={ackBusyId === d.id}
                      className="bg-gold-500 hover:bg-gold-600 text-white"
                      data-testid={`ops-doc-ack-btn-${d.id}`}
                    >
                      <CircleDashed className="w-4 h-4 mr-1" /> {ackBusyId === d.id ? "Saving…" : "I have read this"}
                    </Button>
                  )
                )}
                {canManage && (
                  <Button variant="ghost" onClick={() => removeDoc(d.id)} className="text-red-600 hover:text-red-700" data-testid={`ops-doc-delete-${d.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Phase 3: Compliance Matrix ---------------- */
function ComplianceMatrix({ bookingId, canManage }) {
  const [acks, setAcks] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const load = async () => {
    try { const r = await api.get(`/bookings/${bookingId}/ops-room/acks`); setAcks(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [bookingId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await fetch(`${backendUrl}/api/bookings/${bookingId}/ops-room/compliance-report.pdf`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `secusource-compliance-${bookingId.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      toast.success("Compliance report downloaded");
    } catch (err) {
      toast.error("Failed to generate report. If you are on a shared device, sign in again.");
    } finally { setPdfBusy(false); }
  };

  if (!acks) return <div className="text-white/60 p-6">Loading…</div>;

  const ready = acks.team_ready;
  const pct = acks.total_required > 0 ? Math.round((acks.total_completed / acks.total_required) * 100) : 0;

  return (
    <div className="space-y-4">
      <div className={`card-flat p-4 flex items-center justify-between gap-3 ${ready ? "border-l-4 border-l-gold-500" : "border-l-4 border-l-amber-400"}`}
           data-testid="ops-compliance-status">
        <div className="flex items-center gap-3 min-w-0">
          {ready ? <CheckCircle2 className="w-5 h-5 text-gold-500 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />}
          <div className="min-w-0">
            <div className="font-outfit font-semibold text-white">
              {ready ? "Team Ready · all acknowledgements received" : `Awaiting acknowledgements (${acks.total_completed}/${acks.total_required})`}
            </div>
            <div className="text-xs text-white/60 font-manrope">
              {acks.total_required === 0
                ? "No documents currently require an acknowledgement."
                : `${pct}% complete across ${acks.matrix.length} document(s).`}
            </div>
          </div>
        </div>
        {canManage && (
          <Button onClick={downloadPdf} disabled={pdfBusy} className="bg-navy-900 hover:bg-navy-800 text-white font-manrope font-semibold" data-testid="ops-compliance-pdf-btn">
            <FileDown className="w-4 h-4 mr-1" /> {pdfBusy ? "Building…" : "Download compliance report"}
          </Button>
        )}
      </div>

      {acks.matrix.length === 0 ? (
        <div className="card-flat p-8 text-center text-white/60 font-manrope text-sm">
          Compliance tracking activates as soon as an acknowledgement-required document is uploaded.
        </div>
      ) : acks.matrix.map((row) => (
        <div key={row.doc_id} className="card-flat p-4" data-testid={`ops-compliance-row-${row.doc_id}`}>
          <div className="text-[10px] uppercase tracking-widest text-brand font-outfit font-bold">{row.category_label}</div>
          <div className="font-outfit font-semibold text-white mb-3">{row.title}</div>
          {row.participants.length === 0 ? (
            <div className="text-xs text-white/60 font-manrope">No staff assigned yet.</div>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {row.participants.map((p) => (
                <div key={p.user_id} className={`flex items-center justify-between rounded-md px-3 py-2 text-sm font-manrope ${p.acked_at ? "bg-gold-50 text-gold-800" : "bg-amber-50 text-amber-900"}`}
                     data-testid={`ops-ack-${row.doc_id}-${p.secusource_id}`}>
                  <div className="min-w-0">
                    <div className="font-outfit font-semibold truncate">{p.name}</div>
                    <div className="text-[11px] opacity-70">{p.secusource_id}</div>
                  </div>
                  {p.acked_at ? (
                    <div className="flex items-center gap-1 text-gold-600 font-semibold">
                      <CheckCircle2 className="w-4 h-4" />
                      <span className="text-[11px]">{new Date(p.acked_at).toLocaleString()}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-amber-700 font-semibold">
                      <CircleDashed className="w-4 h-4" /> <span className="text-[11px]">Pending</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {canManage && <AutoEmailCard bookingId={bookingId} />}
    </div>
  );
}

/* ---------------- Phase 5b: Auto-email compliance PDF to client ---------------- */
function AutoEmailCard({ bookingId }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [toEmail, setToEmail] = useState("");
  const [sendAt, setSendAt] = useState("");

  const load = async () => {
    try {
      const r = await api.get(`/bookings/${bookingId}/auto-email-report`);
      setCfg(r.data);
      setEnabled(!!r.data.enabled);
      setToEmail(r.data.to_email || "");
      // Convert ISO to <input type=datetime-local> compatible YYYY-MM-DDTHH:mm
      if (r.data.send_at) {
        const d = new Date(r.data.send_at);
        const pad = (n) => String(n).padStart(2, "0");
        setSendAt(`${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
      }
    } catch (e) { /* silent — endpoint may be missing before deploy */ }
  };
  useEffect(() => { load(); }, [bookingId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      const payload = { enabled };
      if (enabled) {
        if (!toEmail.trim()) { toast.error("Recipient email required"); setSaving(false); return; }
        if (!sendAt) { toast.error("Send-at date/time required"); setSaving(false); return; }
        payload.to_email = toEmail.trim();
        payload.send_at = new Date(sendAt).toISOString();
      }
      await api.put(`/bookings/${bookingId}/auto-email-report`, payload);
      toast.success(enabled ? "Auto-email scheduled" : "Auto-email disabled");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const sendNow = async () => {
    if (!toEmail.trim()) { toast.error("Enter a recipient email first"); return; }
    setSending(true);
    try {
      await api.post(`/bookings/${bookingId}/auto-email-report/send-now`, { to_email: toEmail.trim() });
      toast.success(`Report emailed to ${toEmail}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Send failed");
    } finally { setSending(false); }
  };

  const sent = cfg?.sent_at;
  const scheduledFuture = cfg?.enabled && cfg?.send_at && !sent && new Date(cfg.send_at) > new Date();

  return (
    <div className="card-flat p-5" data-testid="ops-auto-email-card">
      <div className="flex items-center gap-2 mb-2">
        <Mail className="w-4 h-4 text-brand" />
        <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold">Auto-email report to client</div>
      </div>
      <p className="text-xs text-white/60 font-manrope mb-3">
        Automatically email the compliance PDF to a client contact at a scheduled time
        (e.g. 24 hours after the deployment). Turns SecuSource into the accountability layer for your client relationship.
      </p>

      {sent && (
        <div className="mb-3 rounded-md bg-gold-50 border border-gold-200 px-3 py-2 text-sm font-manrope text-gold-800 flex items-center gap-2" data-testid="ops-auto-email-sent">
          <MailCheck className="w-4 h-4 text-gold-500" />
          <div>
            Report emailed
            {cfg.to_email ? <> to <b>{cfg.to_email}</b></> : null}
            {" at "}{new Date(sent).toLocaleString()}.
          </div>
        </div>
      )}
      {scheduledFuture && (
        <div className="mb-3 rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-sm font-manrope text-blue-900 flex items-center gap-2">
          <Clock className="w-4 h-4 text-blue-600" />
          <div>Scheduled for <b>{new Date(cfg.send_at).toLocaleString()}</b> → <b>{cfg.to_email}</b></div>
        </div>
      )}

      <label className="flex items-center gap-2 text-sm text-white font-manrope mb-3">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="ops-auto-email-enable" />
        Enable auto-email
      </label>

      {enabled && (
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <label className="text-xs font-outfit font-bold text-white/70 uppercase tracking-widest">Client email</label>
            <Input
              type="email"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              placeholder="ops@theirclient.com"
              data-testid="ops-auto-email-to"
            />
          </div>
          <div>
            <label className="text-xs font-outfit font-bold text-white/70 uppercase tracking-widest">Send at</label>
            <Input
              type="datetime-local"
              value={sendAt}
              onChange={(e) => setSendAt(e.target.value)}
              data-testid="ops-auto-email-when"
            />
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-4">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-brand hover:bg-brand-hover text-white font-manrope font-semibold"
          data-testid="ops-auto-email-save"
        >
          {saving ? "Saving…" : (enabled ? "Save schedule" : "Turn off")}
        </Button>
        <Button
          onClick={sendNow}
          disabled={sending || !toEmail.trim()}
          variant="outline"
          className="font-manrope font-semibold"
          data-testid="ops-auto-email-send-now"
        >
          <Send className="w-4 h-4 mr-1" /> {sending ? "Sending…" : "Send now"}
        </Button>
      </div>
    </div>
  );
}

/* ---------------- Phase 4: Attendance Roster ---------------- */
function AttendanceRoster({ bookingId, canManage }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [markingId, setMarkingId] = useState(null);
  const [noteBoxFor, setNoteBoxFor] = useState(null);
  const [noteText, setNoteText] = useState("");

  const load = async () => {
    try { const r = await api.get(`/bookings/${bookingId}/ops-room/attendance`); setData(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [bookingId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const isStaff = user && (user.role === "security_professional" || user.role === "lead_professional");
  const meRow = data && data.roster.find((r) => r.is_me);

  const selfCheckin = async () => {
    setBusy(true);
    try {
      await api.post(`/bookings/${bookingId}/ops-room/attendance/self-checkin`, {});
      toast.success("Checked in — thank you, stay safe.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Check-in failed");
    } finally { setBusy(false); }
  };

  const markArrived = async (userId, notes) => {
    setMarkingId(userId);
    try {
      await api.post(`/bookings/${bookingId}/ops-room/attendance/mark`, { user_id: userId, notes: notes || "" });
      toast.success("Marked as arrived");
      setNoteBoxFor(null); setNoteText("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to mark");
    } finally { setMarkingId(null); }
  };

  const clearAttendance = async (userId) => {
    if (!window.confirm("Clear this attendance record? A new arrival timestamp will be created if they re-check-in.")) return;
    setMarkingId(userId);
    try {
      await api.delete(`/bookings/${bookingId}/ops-room/attendance/${userId}`);
      toast.success("Attendance cleared");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to clear");
    } finally { setMarkingId(null); }
  };

  if (!data) return <div className="text-white/60 p-6">Loading…</div>;
  const ready = data.all_arrived;
  const pct = data.total_expected > 0 ? Math.round((data.total_arrived / data.total_expected) * 100) : 0;

  return (
    <div className="space-y-4">
      <div className={`card-flat p-4 flex items-center gap-3 ${ready ? "border-l-4 border-l-gold-500" : "border-l-4 border-l-amber-400"}`}
           data-testid="ops-attendance-status">
        {ready ? <CheckCircle2 className="w-5 h-5 text-gold-500" /> : <AlertCircle className="w-5 h-5 text-amber-500" />}
        <div>
          <div className="font-outfit font-semibold text-white">
            {ready
              ? "All arrived · full team on site"
              : `On site: ${data.total_arrived} of ${data.total_expected}`}
          </div>
          <div className="text-xs text-white/60 font-manrope">
            {data.total_expected === 0
              ? "No security personnel on the roster yet."
              : `${pct}% of the team has checked in.`}
          </div>
        </div>
      </div>

      {isStaff && meRow && (
        <div className="card-flat p-5" data-testid="ops-self-checkin-card">
          {meRow.arrived_at ? (
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-gold-500 flex-shrink-0" />
              <div>
                <div className="font-outfit font-semibold text-white">You are checked in</div>
                <div className="text-xs text-white/60 font-manrope">
                  {new Date(meRow.arrived_at).toLocaleString()}
                  {meRow.method === "marked" && meRow.marked_by_name ? ` · marked by ${meRow.marked_by_name}` : " · self check-in"}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-outfit font-semibold text-white">Confirm you are on site</div>
                <div className="text-xs text-white/60 font-manrope">
                  Tap to record the exact time of arrival. This gets logged in the compliance report.
                </div>
              </div>
              <Button onClick={selfCheckin} disabled={busy} className="bg-gold-500 hover:bg-gold-600 text-white font-manrope font-semibold" data-testid="ops-self-checkin-btn">
                <UserCheck className="w-4 h-4 mr-1" /> {busy ? "Checking in…" : "I have arrived — check in"}
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="space-y-2">
        <div className="text-xs uppercase tracking-widest text-white/60 font-outfit font-bold px-1">Roster</div>
        {data.roster.length === 0 ? (
          <div className="card-flat p-8 text-center text-white/60 font-manrope text-sm">
            No security personnel on the roster yet.
          </div>
        ) : data.roster.map((p) => (
          <div key={p.user_id} className={`card-flat p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${p.arrived_at ? "" : "border-l-4 border-l-amber-300"}`}
               data-testid={`ops-attendance-row-${p.secusource_id}`}>
            <div className="min-w-0">
              <div className="font-outfit font-semibold text-white flex items-center gap-2">
                {p.arrived_at ? <CheckCircle2 className="w-4 h-4 text-gold-500" /> : <CircleDashed className="w-4 h-4 text-amber-500" />}
                {p.name} {p.is_me && <span className="text-[10px] uppercase tracking-widest bg-brand-light text-brand rounded px-2 py-0.5 font-bold">You</span>}
              </div>
              <div className="text-xs text-white/60 font-manrope">
                {p.secusource_id}
                {p.arrived_at ? (
                  <>
                    {" · Arrived "}<span className="text-gold-600 font-semibold">{new Date(p.arrived_at).toLocaleString()}</span>
                    {p.method === "marked" && p.marked_by_name ? ` · marked by ${p.marked_by_name}` : " · self check-in"}
                    {p.notes ? ` · "${p.notes}"` : ""}
                  </>
                ) : (
                  <> · <span className="text-amber-700 font-semibold">Not yet checked in</span></>
                )}
              </div>
            </div>

            {canManage && (
              <div className="flex flex-wrap gap-2">
                {p.arrived_at ? (
                  <Button variant="outline" onClick={() => clearAttendance(p.user_id)} disabled={markingId === p.user_id} data-testid={`ops-attendance-clear-${p.secusource_id}`}>
                    <Trash2 className="w-4 h-4 mr-1" /> Clear
                  </Button>
                ) : noteBoxFor === p.user_id ? (
                  <div className="flex gap-2 items-center">
                    <Input
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="Optional note (e.g. gate B)"
                      className="w-56"
                      data-testid={`ops-attendance-note-${p.secusource_id}`}
                    />
                    <Button
                      onClick={() => markArrived(p.user_id, noteText)}
                      disabled={markingId === p.user_id}
                      className="bg-gold-500 hover:bg-gold-600 text-white"
                      data-testid={`ops-attendance-mark-confirm-${p.secusource_id}`}
                    >
                      <UserCheck className="w-4 h-4 mr-1" /> Save
                    </Button>
                    <Button variant="ghost" onClick={() => { setNoteBoxFor(null); setNoteText(""); }}>Cancel</Button>
                  </div>
                ) : (
                  <>
                    <Button
                      onClick={() => markArrived(p.user_id, "")}
                      disabled={markingId === p.user_id}
                      className="bg-gold-500 hover:bg-gold-600 text-white"
                      data-testid={`ops-attendance-mark-${p.secusource_id}`}
                    >
                      <UserCheck className="w-4 h-4 mr-1" /> Mark arrived
                    </Button>
                    <Button variant="outline" onClick={() => { setNoteBoxFor(p.user_id); setNoteText(""); }}>
                      + Note
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function OpsVideoButton({ bookingId }) {
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(null);
  const [errMsg, setErrMsg] = useState("");
  const [status, setStatus] = useState(""); // "loading" | "joining" | "joined" | ""
  const containerRef = useRef(null);
  const frameRef = useRef(null);
  const joinedRef = useRef(false);

  const startCall = async () => {
    setErrMsg("");
    setStatus("loading");
    try {
      const { data } = await api.post(`/bookings/${bookingId}/ops-room/video-session`);
      setSession(data); setOpen(true);
    } catch (e) {
      setErrMsg(formatApiError(e.response?.data?.detail) || e.message || "Failed to start call");
      setStatus("");
      setOpen(true);
    }
  };

  useEffect(() => {
    if (!open || !session?.room_url) return;
    let cancelled = false;
    let localFrame = null;
    joinedRef.current = false;
    setStatus("joining");
    (async () => {
      try {
        const DailyIframe = (await import("@daily-co/daily-js")).default;
        if (cancelled || !containerRef.current) return;
        // Kill any dangling global Daily instance (React StrictMode / previous modal)
        try {
          const existing = DailyIframe.getCallInstance && DailyIframe.getCallInstance();
          if (existing) {
            try { await existing.leave(); } catch (_e1) { /* noop */ }
            try { existing.destroy(); } catch (_e2) { /* noop */ }
          }
        } catch (_e3) { /* noop */ }
        if (cancelled || !containerRef.current) return;
        try { containerRef.current.innerHTML = ""; } catch (_e4) { /* noop */ }
        localFrame = DailyIframe.createFrame(containerRef.current, {
          showLeaveButton: true,
          iframeStyle: { width: "100%", height: "100%", border: "0", borderRadius: "8px" },
        });
        frameRef.current = localFrame;
        // ONLY auto-close on left-meeting AFTER a successful join — otherwise silent failures snap the modal shut
        localFrame.on("left-meeting", () => { if (joinedRef.current) setOpen(false); });
        localFrame.on("error", (ev) => {
          setErrMsg("Daily.co error: " + (ev?.errorMsg || JSON.stringify(ev || {})));
          setStatus("");
        });
        await localFrame.join({ url: session.room_url, token: session.token });
        if (!cancelled) { joinedRef.current = true; setStatus("joined"); }
      } catch (e) {
        setErrMsg("Failed to load video: " + (e?.message || e?.errorMsg || "unknown"));
        setStatus("");
      }
    })();
    return () => {
      cancelled = true;
      if (localFrame) {
        try { localFrame.leave(); } catch (_e) { /* noop */ }
        try { localFrame.destroy(); } catch (_e) { /* noop */ }
      }
      if (frameRef.current === localFrame) frameRef.current = null;
    };
  }, [open, session]);

  const close = (v) => {
    if (!v && frameRef.current) { try { frameRef.current.leave(); frameRef.current.destroy(); } catch (_e) { /* noop */ } frameRef.current = null; }
    setOpen(v); if (!v) setSession(null);
  };

  return (
    <>
      <Button onClick={startCall} className="bg-brand hover:bg-brand-hover text-white font-manrope font-semibold" data-testid="ops-video-btn">
        <Video className="w-4 h-4 mr-1" /> Start video briefing
      </Button>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent className="max-w-4xl w-[95vw] p-0 overflow-hidden">
          <DialogHeader className="px-5 pt-5"><DialogTitle className="font-outfit"><Video className="w-5 h-5 inline mr-2 text-brand" />Operations Briefing</DialogTitle></DialogHeader>
          {errMsg ? (
            <div className="px-5 pb-5">
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm font-manrope text-amber-900 flex gap-3">
                <AlertCircle className="w-5 h-5 flex-shrink-0 text-amber-600 mt-0.5" />
                <div><div className="font-outfit font-bold mb-1">Video unavailable</div><div>{errMsg}</div></div>
              </div>
              <Button onClick={() => close(false)} className="w-full mt-4 bg-slate-900 hover:bg-slate-800 text-white"><PhoneOff className="w-4 h-4 mr-2" /> Close</Button>
            </div>
          ) : (
            <div ref={containerRef} className="w-full bg-slate-900" style={{ height: "min(70vh, 600px)" }} />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
